import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppLayout } from './components/Layout/AppLayout';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { SkeletonPage } from './components/common/Skeleton';
import { useAuthStore } from './stores/authStore';
import { useUIStore } from './stores/uiStore';

// Lazy-loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Login = lazy(() => import('./pages/Login'));
const ChatPage = lazy(() => import('./pages/Chat'));
const TeamChatPage = lazy(() => import('./pages/TeamChat'));
const AgentsPage = lazy(() => import('./pages/Agents'));
const AgentConfigPage = lazy(() => import('./pages/AgentConfig'));
const ProjectsPage = lazy(() => import('./pages/Projects'));
const ProjectWorkspacePage = lazy(() => import('./pages/ProjectWorkspace'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const AutomationPage = lazy(() => import('./pages/Automation'));
const DeployPage = lazy(() => import('./pages/Deploy'));
const SetupWizard = lazy(() => import('./pages/SetupWizard'));
const OnboardingQuestionnaire = lazy(() => import('./pages/OnboardingQuestionnaire'));
const OnboardingWizard = lazy(() => import('./pages/OnboardingWizard'));
const ProjectSessionPage = lazy(() => import('./pages/ProjectSession'));
const ActiveSpawnsPage = lazy(() => import('./pages/ActiveSpawnsPage'));
const ChannelsPage = lazy(() => import('./pages/Channels'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ApprovalsPage = lazy(() => import('./pages/Approvals'));
const AuditLogPage = lazy(() => import('./pages/AuditLog'));
const ProgressDashboard = lazy(() => import('./pages/ProgressDashboard'));
const TranscriptsPage = lazy(() => import('./pages/Transcripts'));
const HooksPage = lazy(() => import('./pages/Hooks'));
const MainChatPage = lazy(() => import('./pages/MainChat'));

// Advanced / Business panels (real backends)
const TrendArbitragePage = lazy(() => import('./components/Advanced/TrendArbitrage'));
const LegalAgentPage = lazy(() => import('./components/Business/LegalAgent'));
const MonetizationAgentPage = lazy(() => import('./components/Business/MonetizationAgent'));
const DocumentGeneratorPage = lazy(() => import('./components/Business/DocumentGenerator'));
const EmailAgentPage = lazy(() => import('./components/Business/EmailAgent'));
const CrossProjectBrainPage = lazy(() => import('./components/Business/CrossProjectBrain'));
const DemoRecorderPage = lazy(() => import('./components/Advanced/DemoRecorder'));
const PluginManagerPage = lazy(() => import('./components/Settings/PluginManager'));

// Loading fallback with skeleton
const PageLoader: React.FC = () => (
  <SkeletonPage type="cards" />
);

// Hook to check if setup is needed
function useSetupCheck() {
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);

  useEffect(() => {
    fetch('/api/setup/status')
      .then(res => res.json())
      .then(data => {
        setNeedsSetup(data.success ? data.data.needsSetup : false);
      })
      .catch(() => setNeedsSetup(false));
  }, []);

  return needsSetup;
}

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const needsSetup = useSetupCheck();

  // Still loading setup status
  if (needsSetup === null) {
    return <PageLoader />;
  }

  // Redirect to setup wizard if no users exist
  if (needsSetup) {
    return <Navigate to="/setup" replace />;
  }

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If logged in but onboarding not completed — redirect to wizard
  if (user && user.onboardingCompleted === false && location.pathname !== '/onboarding-wizard') {
    return <Navigate to="/onboarding-wizard" replace />;
  }

  return <>{children}</>;
};

// Public route wrapper (redirect to / if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = useAuthStore((s) => s.token);
  const needsSetup = useSetupCheck();

  if (needsSetup === null) {
    return <PageLoader />;
  }

  if (needsSetup) {
    return <Navigate to="/setup" replace />;
  }

  if (token) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const theme = useUIStore((s) => s.theme);

  // Apply theme on mount
  useEffect(() => {
    if (theme === 'light') {
      document.documentElement.setAttribute('data-theme', 'light');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }, [theme]);

  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Setup wizard (no auth required) */}
          <Route path="/setup" element={<SetupWizard />} />
          <Route path="/onboarding" element={<OnboardingQuestionnaire />} />
          <Route path="/onboarding-wizard" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />

          {/* Public routes */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          {/* /register is disabled - first admin created via /setup, all others via admin panel */}
          <Route path="/register" element={<Navigate to="/setup" replace />} />

          {/* Claude-style pure chat — uses its own ChatLayout, NOT AppLayout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainChatPage />
              </ProtectedRoute>
            }
          />

          {/* Protected routes with advanced layout */}
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/chat/:sessionId?" element={<ChatPage />} />
            <Route path="/teamchats/:id?" element={<TeamChatPage />} />
            <Route path="/agents" element={<AgentsPage />} />
            {/* /agents/new must precede /agents/:id so it renders the list page
                (which opens the create-agent modal) instead of the config page
                trying to load an agent whose id is literally "new". */}
            <Route path="/agents/new" element={<AgentsPage />} />
            <Route path="/agents/:id" element={<AgentConfigPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:id" element={<ProjectWorkspacePage />} />
            {/* Autonomous Master-Orchestrator project-sessions */}
            <Route path="/autonomous" element={<ProjectSessionPage />} />
            <Route path="/autonomous/:id" element={<ProjectSessionPage />} />
            <Route path="/autonomous/:id/run" element={<ProjectSessionPage />} />
            <Route path="/spawns" element={<ActiveSpawnsPage />} />
            <Route path="/progress" element={<ProgressDashboard />} />
            <Route path="/automation" element={<AutomationPage />} />
            <Route path="/channels" element={<ChannelsPage />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/deploy" element={<DeployPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="/transcripts" element={<TranscriptsPage />} />
            <Route path="/hooks" element={<HooksPage />} />
            {/* Advanced tools — real backends */}
            <Route path="/trends" element={<TrendArbitragePage />} />
            <Route path="/legal" element={<LegalAgentPage />} />
            <Route path="/monetization" element={<MonetizationAgentPage />} />
            <Route path="/documents" element={<DocumentGeneratorPage />} />
            <Route path="/email-agent" element={<EmailAgentPage />} />
            <Route path="/cross-project" element={<CrossProjectBrainPage />} />
            <Route path="/demo-recorder" element={<DemoRecorderPage />} />
            <Route path="/plugins" element={<PluginManagerPage />} />
            <Route path="/admin" element={<AdminPanel />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
